import { forwardRef, ReactNode } from 'react';
import { clsx } from 'clsx';
import { styled } from '@mui/material/styles';
import { shouldForwardProp as sysShouldForwardProp, SxProps } from '@mui/system';
import { ColorList } from '../theme/colorList';
import Box from '../Box';

import colors from '../theme/colors';
import { useCircleUtilityClasses } from './circleClasses';

export interface CircleProps {
  /**
   * Size (default medium)
   */
  size?: 'x-small' | 'small' | 'medium' | 'large' | 'x-large';

  /**
   * Color (default primary)
   */
  color?: ColorList;

  /**
   * SxProps
   */
  sx?: SxProps;

  children?: ReactNode;
}

const SIZE = {
  XSMALL: 16, //28,
  SMALL: 24, //32,
  MEDIUM: 28, //48,
  LARGE: 32, //64,
  XLARGE: 48, //128,
};

const FONT_SIZE = {
  XSMALL: 10, //28,
  SMALL: 14, //32,
  MEDIUM: 14, //48,
  LARGE: 18, //64,
  XLARGE: 28, //128,
};

const CircleRoot = styled(Box, {
  name: 'O2Circle',
  slot: 'Root',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'size',
})(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
}));

const CircleWrapper = styled('div', {
  name: 'O2Circle',
  slot: 'Wrapper',
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: CircleProps['size'] }>(({ size }) => ({
  aspectRatio: '1',
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  zIndex: '1',
  ...(size === 'x-small' && {
    width: `${SIZE.XSMALL}px`,
  }),
  ...(size === 'small' && {
    width: `${SIZE.SMALL}px`,
  }),
  ...(size === 'medium' && {
    width: `${SIZE.MEDIUM}px`,
  }),
  ...(size === 'large' && {
    width: `${SIZE.LARGE}px`,
  }),
  ...(size === 'x-large' && {
    width: `${SIZE.XLARGE}px`,
  }),
}));

const CircleIcon = styled('div', {
  name: 'O2Circle',
  slot: 'Icon',
  shouldForwardProp: (prop) => prop !== 'size',
})<{ color: CircleProps['color']; size: CircleProps['size'] }>(({ theme, color, size }) => ({
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',

  backgroundColor: colors.solidGray[700],
  color: theme.palette.common.white,
  ...(color && {
    backgroundColor: theme.palette[color].main || theme.palette.primary.main,
    color: theme.palette[color]?.contrastText || theme.palette.common.white,
  }),
  ...(size === 'x-small' && {
    fontSize: `${FONT_SIZE.XSMALL}px`,
    fontWeight: 600,
  }),
  ...(size === 'small' && {
    fontSize: `${FONT_SIZE.SMALL}px`,
    fontWeight: 600,
  }),
  ...(size === 'medium' && {
    fontSize: `${FONT_SIZE.MEDIUM}px`,
    fontWeight: 600,
  }),
  ...(size === 'large' && {
    fontSize: `${FONT_SIZE.LARGE}px`,
    fontWeight: 600,
  }),
  ...(size === 'x-large' && {
    fontSize: `${FONT_SIZE.XLARGE}px`,
    fontWeight: 600,
  }),
}));

const Circle = forwardRef<HTMLDivElement, CircleProps>(
  ({ children, color = 'primary', size = 'medium', ...other }, ref) => {
    const utilityClasses = useCircleUtilityClasses();

    return (
      <CircleRoot ref={ref} className={clsx(utilityClasses.root)} {...other}>
        <CircleWrapper className={clsx(utilityClasses.wrapper)} size={size}>
          <CircleIcon className={clsx(utilityClasses.icon)} size={size} color={color}>
            {children}
          </CircleIcon>
        </CircleWrapper>
      </CircleRoot>
    );
  }
);

Circle.displayName = 'Circle';

export default Circle;
